import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "flowbite-react";
import { HiCheck, HiX, HiArrowLeft, HiArrowRight } from "react-icons/hi";
import { Customer } from "../../models";
import { useWorkspaceData } from "@data";
import {
  CustomerSelectionStep,
  DriverSelectionStep,
  StepIndicator,
  Button,
} from "@components";
import { modalSurface, mapOverlayHeader, modalFooter } from "@design-system";

export interface OrderConfirmationModalProps {
  show: boolean;
  isLoading: boolean;
  orderCustomer: any; // The customer data from the order
  initialCustomerId?: string;
  initialDriverId?: string;
  onClose: () => void;
  onConfirm: (selectedCustomer: Customer | null, driverId: string) => void;
}

// Customer selection logic now moved to CustomerSelectionStep component
// Driver selection logic now moved to DriverSelectionStep component

export const OrderConfirmationModal: React.FC<OrderConfirmationModalProps> = ({
  show,
  isLoading,
  orderCustomer,
  initialCustomerId,
  initialDriverId,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(["orders", "common"]);

  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [createNewCustomer, setCreateNewCustomer] = useState<boolean>(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string>("");
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [initialSelectionApplied, setInitialSelectionApplied] = useState(false);

  // Fetch data from the centrally-loaded data store.
  const {
    customers: wsCustomers,
    drivers: wsDrivers,
    loading,
  } = useWorkspaceData();

  // Use stable data references to prevent flickering during confirmation
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);

  // Update data only when new data is available and we're not confirming
  useEffect(() => {
    if (!loading.customers && !isLoading) {
      setCustomers(wsCustomers);
    }
  }, [wsCustomers, loading.customers, isLoading]);

  useEffect(() => {
    if (!loading.drivers && !isLoading) {
      setDrivers(wsDrivers);
    }
  }, [wsDrivers, loading.drivers, isLoading]);

  // Reset state only when modal first opens (not during confirmation)
  useEffect(() => {
    if (show && !isLoading) {
      setCurrentStep(1);
      setSelectedCustomer(null);
      setCreateNewCustomer(false);
      setSelectedDriverId("");
      setInitialDataLoaded(false);
      setInitialSelectionApplied(false);
    }
  }, [show]);

  useEffect(() => {
    if (
      !show ||
      isLoading ||
      initialSelectionApplied ||
      loading.customers ||
      loading.drivers
    ) {
      return;
    }

    if (initialCustomerId) {
      const initialCustomer = wsCustomers.find(
        (customer) => customer.id === initialCustomerId,
      );
      if (initialCustomer) {
        setSelectedCustomer(initialCustomer);
      }
    }
    if (
      initialDriverId &&
      wsDrivers.some(
        (driver) =>
          driver.id === initialDriverId || driver.email === initialDriverId,
      )
    ) {
      const initialDriver = wsDrivers.find(
        (driver) =>
          driver.id === initialDriverId || driver.email === initialDriverId,
      );
      setSelectedDriverId(initialDriver?.email || "");
    }
    setInitialSelectionApplied(true);
  }, [
    initialCustomerId,
    initialDriverId,
    initialSelectionApplied,
    isLoading,
    loading.customers,
    loading.drivers,
    show,
    wsCustomers,
    wsDrivers,
  ]);

  // Track when initial data is loaded
  useEffect(() => {
    if (
      !loading.customers &&
      !loading.drivers &&
      !initialDataLoaded &&
      !isLoading
    ) {
      setInitialDataLoaded(true);
    }
  }, [loading.customers, loading.drivers, initialDataLoaded, isLoading]);

  const handleCustomerSelect = (
    customer: Customer | null,
    createNew: boolean,
  ) => {
    setSelectedCustomer(customer);
    setCreateNewCustomer(createNew);
  };

  const handleDriverSelect = (driverId: string) => {
    setSelectedDriverId(driverId);
  };

  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const handleConfirm = () => {
    if (selectedDriverId) {
      onConfirm(createNewCustomer ? null : selectedCustomer, selectedDriverId);
    }
  };

  const canProceedToStep2 = selectedCustomer || createNewCustomer;
  const canConfirm =
    selectedDriverId && (selectedCustomer || createNewCustomer);

  const stepTitles = [t("orders:select_customer"), t("common:select_driver")];

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Prevent backdrop click during loading */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={!isLoading ? onClose : undefined}
      ></div>
      <div className={`relative ${modalSurface} w-full max-w-2xl max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 ${mapOverlayHeader}`}>
          <h3 className="text-lg font-semibold text-text">
            {t("orders:assign_driver_and_confirm")}
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className={`text-muted hover:text-gray-600 dark:hover:text-gray-300 transition-opacity ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
          {/* Step Indicator */}
          <StepIndicator
            currentStep={currentStep}
            totalSteps={2}
            stepTitles={stepTitles}
          />

          {/* Show loading only on initial load, not during confirmation */}
          {!initialDataLoaded && (loading.customers || loading.drivers) ? (
            <div className="flex justify-center items-center py-8">
              <Spinner size="lg" />
              <span className="ml-3 text-muted">
                {t("common:loading")}...
              </span>
            </div>
          ) : currentStep === 1 ? (
            <CustomerSelectionStep
              customers={customers}
              orderCustomer={orderCustomer}
              selectedCustomer={selectedCustomer}
              createNew={createNewCustomer}
              onCustomerSelect={handleCustomerSelect}
            />
          ) : (
            <DriverSelectionStep
              drivers={drivers}
              selectedDriverId={selectedDriverId}
              onDriverSelect={handleDriverSelect}
            />
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-between items-center p-6 ${modalFooter}`}>
          <div className="flex space-x-3">
            <Button
              color="gray"
              onClick={onClose}
              disabled={isLoading}
              className="focus:ring-0"
            >
              <HiX className="w-4 h-4 mr-2" />
              {t("common:cancel")}
            </Button>
            {currentStep === 2 && (
              <Button
                color="gray"
                onClick={handleBack}
                disabled={isLoading}
                className="focus:ring-0"
              >
                <HiArrowLeft className="w-4 h-4 mr-2" />
                {t("common:back")}
              </Button>
            )}
          </div>

          <div className="flex space-x-3">
            {currentStep === 1 ? (
              <Button
                color="primary"
                onClick={handleNext}
                disabled={!canProceedToStep2}
              >
                {t("common:next")}
                <HiArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                color="primary"
                onClick={handleConfirm}
                disabled={!canConfirm || isLoading}
              >
                {isLoading ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <HiCheck className="w-4 h-4 mr-2" />
                )}
                {t("orders:confirm_order")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
